import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import Checklist from './Checklist'
import Store from './Store'
import { ProviderChecklistItem } from '../providers/types'

export default class ChecklistItem {
  id = ''
  name = ''
  description?: string
  position = 0
  complete = false
  completed_at: Date | null = null
  created_at: Date = new Date()
  sectionId: string | null = null
  skipped = false
  store: Store
  checklist: Checklist
  filename?: string
  filetype?: string
  descriptionFilename?: string
  descriptionFiletype?: string
  requiresMedia = false
  fileUrl?: string
  descriptionFileUrl?: string

  constructor(store: Store, checklist: Checklist) {
    makeObservable(this, {
      name: observable,
      description: observable,
      complete: observable,
      completed_at: observable,
      skipped: observable,
      filename: observable,
      filetype: observable,
      descriptionFilename: observable,
      descriptionFiletype: observable,
      requiresMedia: observable,
      fileUrl: observable,
      descriptionFileUrl: observable,
      toggleComplete: action,
      setAsSkipped: action,
      fromProvider: action,
      created_at: false,
      addFile: action,
      addDescriptionFile: action,
      canUploadFile: computed,
      canComplete: computed,
      hasFile: computed,
      hasDescriptionFile: computed,
    })
    this.created_at = new Date()
    this.store = store
    this.checklist = checklist
  }

  fromProvider = (data: ProviderChecklistItem) => {
    this.id = data.id
    this.name = data.name
    this.description = data.description
    this.position = data.position
    this.complete = data.complete
    this.skipped = data.skipped
    this.sectionId = data.sectionId
    this.filename = data.filename
    this.filetype = data.filetype
    this.descriptionFilename = data.descriptionFilename
    this.descriptionFiletype = data.descriptionFiletype
    this.requiresMedia = data.requiresMedia ?? false
    return this
  }

  update = (data: Partial<ProviderChecklistItem>) => {
    const payload: Partial<ProviderChecklistItem> = {}
    if (data.name !== undefined) {
      this.name = data.name
      payload.name = data.name
    }
    if (data.description !== undefined) {
      this.description = data.description
      payload.description = data.description
    }
    if (data.descriptionFilename !== undefined) {
      this.descriptionFilename = data.descriptionFilename
      payload.descriptionFilename = data.descriptionFilename
    }
    if (data.descriptionFiletype !== undefined) {
      this.descriptionFiletype = data.descriptionFiletype
      payload.descriptionFiletype = data.descriptionFiletype
    }
    if (data.requiresMedia !== undefined) {
      this.requiresMedia = data.requiresMedia
      payload.requiresMedia = data.requiresMedia
    }

    void this.store.provider.updateChecklistItem(
      this.checklist.id,
      this.id,
      payload
    )
  }

  addFile = async (filename: string, filetype: string) => {
    await this.store.provider.updateChecklistItem(this.checklist.id, this.id, {
      filename,
      filetype,
    })
    this.filename = filename
    this.filetype = filetype
  }

  addDescriptionFile = async (filename: string, filetype: string) => {
    await this.store.provider.updateChecklistItem(this.checklist.id, this.id, {
      descriptionFilename: filename,
      descriptionFiletype: filetype,
    })
    this.descriptionFilename = filename
    this.descriptionFiletype = filetype
  }

  markAsComplete = () => {
    if (!this.canComplete) return false
    const complete = true
    this.skipped = false
    this.complete = complete
    this.completed_at = complete ? new Date() : null
    void this.store.provider.updateChecklistItem(this.checklist.id, this.id, {
      complete: this.complete,
      skipped: this.skipped,
    })
    return true
  }

  toggleComplete = () => {
    if (this.skipped) {
      this.skipped = false
      this.complete = false
      this.completed_at = null
    } else {
      const complete = !this.complete
      if (complete && !this.canComplete) return false
      if (!complete) {
        this.skipped = false
      }
      this.complete = complete
      this.completed_at = complete ? new Date() : null
    }
    void this.store.provider.updateChecklistItem(this.checklist.id, this.id, {
      complete: this.complete,
      skipped: this.skipped,
    })
    return true
  }

  setAsSkipped = () => {
    this.skipped = true
    void this.store.provider.updateChecklistItem(this.checklist.id, this.id, {
      skipped: true,
    })
  }

  getUploadUrl = async (filename: string) => {
    return this.store.provider.getChecklistItemUploadUrl(
      this.checklist.id,
      this.id,
      { filename }
    )
  }

  getFileUrl = async () => {
    const data = await this.store.provider.getChecklistItemFileUrl(
      this.checklist.id,
      this.id
    )

    this.fileUrl = data.url

    return data
  }

  getDescriptionFileUrl = async () => {
    const data = await this.store.provider.getChecklistItemDescriptionFileUrl(
      this.checklist.id,
      this.id
    )

    runInAction(() => {
      this.descriptionFileUrl = data.url
    })

    return data
  }

  get canUploadFile() {
    return !this.skipped
  }

  get canComplete() {
    return !this.requiresMedia || this.hasFile
  }

  get hasFile() {
    return Boolean(this.filename)
  }

  get hasDescriptionFile() {
    return Boolean(this.descriptionFilename)
  }

  static create(checklist: Checklist, name: string) {
    const store = checklist.store
    const item = new ChecklistItem(store, checklist)
    item.name = name
    store.provider
      .createChecklistItem(checklist.id, {
        id: item.id,
        name: item.name,
      })
      .then((template) => item.fromProvider(template))
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
    return item
  }
}
